import { Badge } from '@clearpath/design-system/ui/badge';
import { Button } from "@clearpath/design-system/ui/button";
import { useFederationLinksQuery } from "../__generated__/graphql";
import {
  directionLabel,
  federationStatusLabel,
  federationStatusTone,
  workTypeLabel,
} from "../lib/federation-format";

/**
 * Federation connections registry.
 *
 * Read-only listing of active + pending federation_links. Accepting new
 * links and editing event_visibility_config lives in apps/main's admin
 * surface (clearpath.federation.accept permission), NOT here — vendor
 * staff can see the connections they work under, but not mutate them.
 */
export function FederationLinksPage() {
  const { data, loading, error, refetch } = useFederationLinksQuery({
    variables: { page: { first: 100 } },
  });

  if (loading && !data) {
    return (
      <section aria-label="Federation connections" className="inbox-page">
        <p className="loading-row">Loading connections…</p>
      </section>
    );
  }
  if (error) {
    return (
      <section aria-label="Federation connections" className="inbox-page">
        <div className="notice notice-danger" role="alert">
          <p>Couldn’t load your connections.</p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => void refetch()}
          >
            Retry
          </Button>
        </div>
      </section>
    );
  }

  const links = (data?.federationLinks?.edges ?? []).map((e) => e.node);

  return (
    <section aria-label="Federation connections" className="inbox-page">
      <header className="page-head">
        <div>
          <h1>Connections</h1>
          <p className="page-sub">
            Federation links this tenant participates in. Accepting a new link
            or changing what crosses the boundary is done by an admin in the
            main app.
          </p>
        </div>
      </header>

      {links.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon" aria-hidden="true">
            🤝
          </div>
          <h2>No active connections yet</h2>
          <p>
            When a partner lab federates work to you, the connection appears
            here. New links are accepted by an administrator in the main
            ClearPath app (permission&nbsp;
            <code>clearpath.federation.accept</code>).
          </p>
        </div>
      ) : (
        <div className="table-card">
          <table className="inbox">
            <thead>
              <tr>
                <th scope="col">Partner</th>
                <th scope="col">Direction</th>
                <th scope="col">Status</th>
                <th scope="col">Allowed work</th>
                <th scope="col">Accepted</th>
              </tr>
            </thead>
            <tbody>
              {links.map((link) => (
                <tr key={link.id}>
                  <td>
                    <span
                      className="origin-tenant-badge"
                      title={`Partner tenant id: ${link.partnerTenantId}`}
                    >
                      <span aria-hidden="true">◈</span>
                      <span>
                        {link.label?.trim() ||
                          link.partnerTenantName?.trim() ||
                          `Tenant ${link.partnerTenantId.replace(/-/g, "").slice(0, 4)}`}
                      </span>
                    </span>
                  </td>
                  <td>{directionLabel(link.direction)}</td>
                  <td>
                    <Badge variant={federationStatusTone(link.status)}>
                      {federationStatusLabel(link.status)}
                    </Badge>
                  </td>
                  <td>
                    {link.allowedWorkTypes.length > 0 ? (
                      <span className="case-chips">
                        {link.allowedWorkTypes.map((w) => (
                          <span key={w} className="chip">
                            {workTypeLabel(w)}
                          </span>
                        ))}
                      </span>
                    ) : (
                      <span className="cell-empty">—</span>
                    )}
                  </td>
                  <td>
                    {link.acceptedAt ? (
                      <time dateTime={link.acceptedAt}>
                        {new Date(link.acceptedAt).toLocaleDateString()}
                      </time>
                    ) : (
                      <span className="cell-empty">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
